import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon, DevicePhoneMobileIcon } from '@heroicons/react/24/outline';
import { request } from '../utils/request';

interface QRUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUploadComplete?: (images: string[]) => void;
}

const QRUploadModal: React.FC<QRUploadModalProps> = ({ 
  isOpen, 
  onClose, 
  onUploadComplete 
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState<string>('');
  const [uploadUrl, setUploadUrl] = useState<string>('');
  const [isGeneratingQR, setIsGeneratingQR] = useState(false);

  // Generate QR code
  const generateQRCode = async () => {
    try {
      setIsGeneratingQR(true);
      
      // Call API to generate QR code
      const response: any = await request({
        url: 'upload/generate-qr',
        method: 'POST'
      });

      if (response.success && response.result) {
        const { token, uploadUrl, qrCodeDataURL } = response.result;
        setUploadUrl(uploadUrl);
        setQrCodeUrl(qrCodeDataURL); // Use base64 format QR code
      } else {
        console.error('Failed to generate QR code:', response.message);
      }
    } catch (error) {
      console.error('Error generating QR code:', error);
    } finally {
      setIsGeneratingQR(false);
    }
  };

  // Generate QR code when modal opens
  useEffect(() => {
    if (isOpen) {
      generateQRCode();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleDoneUploading = () => {
    setIsUploading(true);
    // Here you can add logic to check upload status
    // Simulate upload completion
    setTimeout(() => {
      setIsUploading(false);
      onUploadComplete?.([]);
      onClose();
    }, 1000);
  };

  // Use portal to render at the top level of the DOM
  return createPortal(
    <div className="fixed inset-0  flex items-center justify-center" style={{ zIndex: 1000 }}>
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-lg p-6 max-w-md w-full mx-4">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 left-4 text-gray-500 hover:text-gray-700 transition-colors"
        >
          <XMarkIcon className="w-6 h-6" />
        </button>

        {/* Instructions */}
        <div className="flex items-center mb-6 mt-2">
          <DevicePhoneMobileIcon className="w-5 h-5 text-gray-600 mr-2" />
          <p className="text-gray-800 text-sm">
            Scan the QR code using your phone's camera to select and upload your photos.
          </p>
        </div>

        {/* QR Code */}
        <div className="flex justify-center mb-6">
          <div className="relative">
            <div className="w-48 h-48 bg-white border-2 border-gray-300 flex items-center justify-center">
              {isGeneratingQR ? (
                <div className="flex flex-col items-center justify-center">
                  <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mb-2"></div>
                  <p className="text-xs text-gray-500">Generating...</p>
                </div>
              ) : qrCodeUrl ? (
                <img 
                  src={qrCodeUrl} 
                  alt="Upload QR Code"
                  className="w-44 h-44"
                  onError={(e) => {
                    console.error('QR code failed to load');
                    e.currentTarget.style.display = 'none';
                  }}
                />
              ) : (
                <div className="flex flex-col items-center justify-center">
                  <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center mb-2">
                    <span className="text-gray-500 text-sm">?</span>
                  </div>
                  <p className="text-xs text-gray-500">Generation Failed</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Secondary Instructions */}
        <p className="text-center text-gray-800 text-sm mb-4">
          Once you've finished uploading, click the button below to see them.
        </p>

        {/* Refresh QR Code Button */}
        {!isGeneratingQR && (
          <div className="text-center mb-4">
            <button
              onClick={generateQRCode}
              className="text-xs text-blue-600 hover:text-blue-800 underline"
            >
              Regenerate QR Code
            </button>
          </div>
        )}

        {/* Action Button */}
        <button
          onClick={handleDoneUploading}
          disabled={isUploading}
          className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center"
        >
          {isUploading ? (
            <>
              <svg className="animate-spin w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Checking...
            </>
          ) : (
            'Done Uploading'
          )}
        </button>
      </div>
    </div>,
    document.body
  );
};

export default QRUploadModal;
